import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';

import { ErrorState } from '@/components/states';
import { registerSessionExpiredHandler } from '@/lib/api/client';
import { queryKeys } from '@/lib/query/keys';

import { useSession } from './auth-queries';
import { SignInScreen } from './sign-in-screen';

/**
 * The authentication boundary in front of the whole application (spec R7.5).
 *
 * While the session is loading it shows a spinner; unauthenticated, it renders
 * the sign-in screen *instead of* the router, so no route ever mounts without a
 * user; authenticated, it renders the app. Keeping it above the router — but
 * below the `QueryClientProvider` — is what lets the axios layer's
 * session-expired handler flip the app back to sign-in by clearing one query.
 */
export const AuthGate = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const session = useSession();

  /*
   * Wire the axios interceptor's "refresh failed" hook to the cache: dropping
   * the session query to `null` re-renders this gate onto the sign-in screen.
   */
  useEffect(() => {
    registerSessionExpiredHandler(() => {
      queryClient.setQueryData(queryKeys.auth.session(), null);
    });

    return () => {
      registerSessionExpiredHandler(null);
    };
  }, [queryClient]);

  if (session.isPending) {
    return (
      <div
        className="flex min-h-dvh items-center justify-center bg-background"
        aria-live="polite"
      >
        <Loader2
          aria-label="Loading"
          className="size-6 animate-spin text-muted-foreground"
        />
      </div>
    );
  }

  /*
   * A non-401 failure — the API is down or unreachable — is not "signed out".
   * Saying so would send the user to Google and back into the same wall, so it
   * gets an error with a retry instead.
   */
  if (session.isError) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background px-6">
        <ErrorState
          className="max-w-lg"
          title="Could not reach Atlas"
          error={session.error}
          onRetry={() => {
            void session.refetch();
          }}
        />
      </div>
    );
  }

  if (session.data === null) {
    return <SignInScreen />;
  }

  return <>{children}</>;
};
